import { Component, Input } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { Utils } from "@serasa/shared/utils/utils";
import { EmailDomainValidator } from "@serasa/shared/validators";

export interface EmailIdEntryForm {
  email: FormControl<string>;
  accountName: FormControl<string>;
}

export const ACCOUNT_NAME_MAX_LENGTH = 20;

/**
 * Cria uma linha da lista (e-mail + identificação). A validação de e-mail só é aplicada
 * quando `withEmailValidation` é true — usado apenas na primeira linha (obrigatória). As
 * demais linhas não têm validação de e-mail: ficam opcionais e são ignoradas se vazias.
 */
export function createEmailIdEntry(
  withEmailValidation = false,
): FormGroup<EmailIdEntryForm> {
  const emailValidators = withEmailValidation
    ? [new EmailDomainValidator(Utils.emailRegex()).validate()]
    : [];

  return new FormGroup<EmailIdEntryForm>({
    email: new FormControl("", {
      nonNullable: true,
      validators: emailValidators,
    }),
    accountName: new FormControl("", {
      nonNullable: true,
      validators: [Validators.maxLength(ACCOUNT_NAME_MAX_LENGTH)],
    }),
  });
}

@Component({
  selector: "pl-email-id-list",
  templateUrl: "./email-id-list.component.html",
  styleUrls: ["./email-id-list.component.scss"],
  standalone: false,
})
export class EmailIdListComponent {
  private static readonly ALLOWED_CHAR_REGEX = /^[a-zA-Z0-9\s]$/;
  private static readonly DISALLOWED_CHARS_REGEX = /[^a-zA-Z0-9\s]/g;

  @Input({ required: true }) entriesForm!: FormArray<
    FormGroup<EmailIdEntryForm>
  >;
  @Input() maxEntries = 10;
  @Input() disabled = false;

  get rows(): FormGroup<EmailIdEntryForm>[] {
    return this.entriesForm.controls;
  }

  get remaining(): number {
    return Math.max(this.maxEntries - this.rows.length, 0);
  }

  get canAddRow(): boolean {
    return !this.disabled && this.rows.length < this.maxEntries;
  }

  public addRow(): void {
    if (!this.canAddRow) {
      return;
    }
    this.entriesForm.push(createEmailIdEntry());
  }

  public emailControl(row: FormGroup<EmailIdEntryForm>): FormControl<string> {
    return row.controls.email;
  }

  public accountNameControl(
    row: FormGroup<EmailIdEntryForm>,
  ): FormControl<string> {
    return row.controls.accountName;
  }

  public showEmailError(row: FormGroup<EmailIdEntryForm>): boolean {
    const control = row.controls.email;
    return control.invalid && (control.touched || control.dirty);
  }

  public showNameWarning(row: FormGroup<EmailIdEntryForm>): boolean {
    const control = row.controls.accountName;
    return control.hasError("maxlength") && (control.touched || control.dirty);
  }

  /**
   * Mantém o e-mail sempre em caixa baixa, independentemente de o valor ter sido
   * digitado, colado, arrastado ou preenchido por autofill. Como converter para
   * minúsculas não altera o tamanho do texto, a posição do cursor é preservada.
   */
  public lowercaseEmail(
    event: Event,
    row: FormGroup<EmailIdEntryForm>,
  ): void {
    const input = event.target as HTMLInputElement;
    const lower = input.value.toLowerCase();

    if (lower === input.value) {
      return;
    }

    const start = input.selectionStart;
    const end = input.selectionEnd;
    row.controls.email.setValue(lower);
    input.setSelectionRange(start, end);
  }

  /**
   * Bloqueia a digitação de caracteres especiais e acentos no campo de
   * identificação, permitindo apenas letras, números e espaço — alinhado com
   * a regex do `AlphanumericRegexValidator`.
   */
  public validateSpecialChars(event: KeyboardEvent): void {
    // Não interfere em atalhos (Ctrl/Cmd + tecla), ex.: colar, selecionar.
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Teclas de controle (Enter, Backspace, setas...) têm key com mais de um
    // caractere e devem ser sempre permitidas.
    if (event.key.length === 1 && !EmailIdListComponent.ALLOWED_CHAR_REGEX.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Sanitiza o conteúdo colado no campo de identificação, removendo caracteres
   * especiais e acentos (mantém apenas letras, números e espaço). Insere o texto
   * já limpo na posição do cursor, respeitando a seleção atual e o `maxlength`.
   */
  public sanitizePaste(
    event: ClipboardEvent,
    row: FormGroup<EmailIdEntryForm>,
  ): void {
    event.preventDefault();

    const pasted = event.clipboardData?.getData("text") ?? "";
    const sanitized = pasted.replace(
      EmailIdListComponent.DISALLOWED_CHARS_REGEX,
      "",
    );

    const input = event.target as HTMLInputElement;
    const control = row.controls.accountName;
    const current = control.value ?? "";
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? current.length;
    const maxLen =
      input.maxLength > 0 ? input.maxLength : ACCOUNT_NAME_MAX_LENGTH;

    const next = (current.slice(0, start) + sanitized + current.slice(end)).slice(
      0,
      maxLen,
    );
    control.setValue(next);
    control.markAsDirty();

    // Reposiciona o cursor logo após o trecho colado.
    const caret = Math.min(start + sanitized.length, next.length);
    input.setSelectionRange(caret, caret);
  }
}
